<script lang="ts">
    import type { ApiSchemas } from '$lib/api/shopwareApi';
    import { productExtraMedia, type ProductMediaItem } from '$lib/components/productDetails/productExtraMedia';
    import 'swiper/css';
    import 'swiper/css/a11y';
    import 'swiper/css/thumbs';
    import { onMount } from 'svelte';
    import { Swiper } from 'swiper';
    import { A11y, Navigation } from 'swiper/modules';
    import R2Image from '$lib/components/util/R2Image.svelte';

    export let product: ApiSchemas['Product'];
    export let productVariant: ApiSchemas['Product'] | undefined;
    export let vvmLine: string | undefined;

    let mediaList: ProductMediaItem[];
    $: mediaList = buildMediaList(product, productVariant);
    $: if (mediaList) rebuildSwiper();

    function buildMediaList(
        product: NonNullable<ApiSchemas['Product']>,
        productVariant: ApiSchemas['Product'] | undefined
    ) {
        const list: ProductMediaItem[] = [];

        if (!productVariant && productExtraMedia[product.productNumber?.toLowerCase()]) {
            list.push(...productExtraMedia[product.productNumber?.toLowerCase()]);
        }

        if (productVariant?.media?.length) {
            list.push(
                ...(productVariant.media as unknown as ApiSchemas['ProductMedia'][])
                    .filter((e) => !!e.media)
                    .map(
                        (e) =>
                            ({
                                id: e.media!.id!,
                                type: 'media',
                                data: e.media!,
                                position: e.position
                            }) satisfies ProductMediaItem
                    )
            );
        } else if (product.media?.length) {
            list.push(
                ...(product.media as unknown as ApiSchemas['ProductMedia'][])
                    .filter((e) => !!e.media)
                    .map(
                        (e) =>
                            ({
                                id: e.media!.id!,
                                type: 'media',
                                data: e.media!,
                                position: e.position
                            }) satisfies ProductMediaItem
                    )
            );
        }

        list.sort((a, b) => a.position - b.position);

        return list;
    }

    let productImageSwiperElem: HTMLDivElement;

    let productImageSwiper: Swiper;

    function rebuildSwiper() {
        if (productImageSwiper) {
            productImageSwiper.destroy();
        }

        productImageSwiper = new Swiper(productImageSwiperElem, {
            modules: [A11y, Navigation],
            slidesPerView: 'auto',
            a11y: {
                firstSlideMessage: 'Erstes Produktbild',
                lastSlideMessage: 'Letztes Produktbild',
                nextSlideMessage: 'Nächstes Produktbild',
                paginationBulletMessage: 'Zu Produktbild {{index}} scrollen',
                prevSlideMessage: 'Vorheriges Produktbild'
            }
        });
    }

    onMount(() => {
        rebuildSwiper();

        return () => {
            productImageSwiper.destroy();
        };
    });
</script>

<div
    bind:this={productImageSwiperElem}
    class="swiper lg:min-h-[44.875rem] !m-0 w-full lg:max-w-xl !relative select-none"
>
    <div class="swiper-wrapper">
        {#each mediaList as media (media.id)}
            <div class="swiper-slide">
                {#if media.type === 'media'}
                    <div class="h-96 lg:h-[44.875rem] w-full">
                        <R2Image
                            imageId={media?.data?.url}
                            widths={[750]}
                            alt={product.cover?.media?.alt}
                            loading="eager"
                            decoding="sync"
                            class="h-full w-full object-contain px-3 xl:px-20"
                        />
                    </div>
                {/if}
            </div>
        {/each}
    </div>
    {#if mediaList.length > 1}
        <div class="!absolute left-0 bottom-5 !h-fit swiper-wrapper flex justify-between lg:justify-start gap-5">
            <button class="swiper-button-next size-10" on:click={() => productImageSwiper.slidePrev()}>
                {#if vvmLine === 'vv'}
                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <rect width="39.9987" height="39.9987" transform="matrix(-1 0 0 1 39.9985 0)" fill="#B42133" />
                        <path
                            d="M30.832 19.5664L24.669 13.4034L8.06569 19.5664L24.669 25.7294L30.832 19.5664Z"
                            fill="#EFE6E7"
                        />
                    </svg>
                {:else if vvmLine === 'prestige'}
                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <rect
                            x="-0.5"
                            y="0.5"
                            width="38.9987"
                            height="38.9987"
                            transform="matrix(-1 0 0 1 38.999 0)"
                            stroke="#477785"
                        />
                        <path
                            d="M30.1249 19.5664L24.5423 13.9838L9.50251 19.5664L24.5423 25.149L30.1249 19.5664Z"
                            stroke="#477785"
                        />
                    </svg>
                {:else}
                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <rect width="39.9987" height="39.9987" transform="matrix(-1 0 0 1 39.9988 0)" fill="#CAA75E" />
                        <path
                            d="M30.8323 19.5664L24.6693 13.4034L8.06593 19.5664L24.6693 25.7294L30.8323 19.5664Z"
                            fill="#FDFCFD"
                        />
                    </svg>
                {/if}
            </button>
            <button class="swiper-button-prev size-10" on:click={() => productImageSwiper.slideNext()}>
                {#if vvmLine === 'classic'}
                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <rect x="-0.0012207" width="39.9987" height="39.9987" fill="#CAA75E" />
                        <path
                            d="M9.16528 19.5664L15.3283 13.4034L31.9316 19.5664L15.3283 25.7294L9.16528 19.5664Z"
                            fill="#FDFCFD"
                        />
                    </svg>
                {:else if vvmLine === 'vv'}
                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <rect x="-0.00146484" width="39.9987" height="39.9987" fill="#B42133" />
                        <path
                            d="M9.16504 19.5664L15.328 13.4034L31.9314 19.5664L15.328 25.7294L9.16504 19.5664Z"
                            fill="#EFE6E7"
                        />
                    </svg>
                {:else if vvmLine === 'prestige'}
                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <rect x="0.499023" y="0.5" width="38.9987" height="38.9987" stroke="#477785" />
                        <path
                            d="M9.87312 19.5664L15.4557 13.9838L30.4955 19.5664L15.4557 25.149L9.87312 19.5664Z"
                            stroke="#477785"
                        />
                    </svg>
                {/if}
            </button>
        </div>
    {/if}
</div>
